import type {
  NotificationRecipient,
  NotificationRecipientIdInput,
} from '@/lib/services/notification-recipients/notification-recipients.types'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can } from '@/lib/casl'
import { NotificationChannelType } from '@/lib/enums/NotificationChannelType'
import { serverFieldErrors, useAppForm } from '@/lib/form'
import { notificationChannelLabelOverrides } from '@/lib/notification-channel-labels'
import {
  useCreateNotificationRecipientMutation,
  useDeleteNotificationRecipientMutation,
  useSendRecipientVerificationMutation,
  useUpdateNotificationRecipientMutation,
} from '@/lib/services/notification-recipients/notification-recipients.query'
import { enumToSelectOptions, getErrorMessage } from '@/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import { useId } from 'react'
import { toast } from 'sonner'
import VerificationSection from '../notification-recipients/VerificationSection'
import { Typography } from '@/components/ui/typography'
import DeleteButton from './DeleteButton'
import SaveBar from './SaveBar'

type NotificationRecipientFormValues = {
  channel: NotificationRecipient['channel']
  value: string
  label: string
  enabled: boolean
}

// Preserve both wrapped and unwrapped create IDs already accepted by this form.
type CreatedRecipientIdView = {
  id?: NotificationRecipientIdInput
  data?: { id?: NotificationRecipientIdInput }
}
function createdRecipientId(
  response: CreatedRecipientIdView | null | undefined
): NotificationRecipientIdInput | undefined {
  return response?.data?.id || response?.id
}

const buildDefaultFormValues = (
  recipient: NotificationRecipient | null | undefined
): NotificationRecipientFormValues => {
  return {
    channel: recipient?.channel ?? NotificationChannelType.Text.Value,
    value: recipient?.value ?? '',
    label: recipient?.label ?? '',
    enabled: recipient?.enabled ?? true,
  }
}

const channelOptions = enumToSelectOptions(NotificationChannelType, {
  includeIcons: true,
  labelOverrides: notificationChannelLabelOverrides,
})

export default function NotificationRecipientForm({
  recipient,
}: {
  recipient?: NotificationRecipient | null
}) {
  const id = useId()
  const navigate = useNavigate()
  const path = usePathHelper()
  const createMutation = useCreateNotificationRecipientMutation()
  const updateMutation = useUpdateNotificationRecipientMutation()
  const deleteMutation = useDeleteNotificationRecipientMutation({ cleanupOnSuccess: false })
  const sendVerificationMutation = useSendRecipientVerificationMutation()

  const isEditing = !!recipient?.id

  const form = useAppForm({
    defaultValues: buildDefaultFormValues(recipient),
    validators: {
      onSubmitAsync: async ({ value, formApi }) => {
        try {
          if (isEditing) {
            await updateMutation.mutateAsync({ id: recipient.id, ...value })
            toast.success('Recipient updated successfully')
            setTimeout(() => formApi.reset())
          } else {
            const response = await createMutation.mutateAsync(value)
            const recipientId = createdRecipientId(response)

            if (recipientId) {
              try {
                const verificationResponse = await sendVerificationMutation.mutateAsync(recipientId)
                toast.success(verificationResponse?.message || 'Verification sent successfully')
              } catch (verificationError) {
                toast.error('Recipient saved, but verification failed', {
                  description: getErrorMessage(verificationError),
                })
              }
            } else {
              toast.success('Recipient created successfully')
            }

            formApi.reset()
            const to = recipientId
              ? path.notifications.show(recipientId).link
              : path.notifications.index().link
            return navigate({ ...to, ignoreBlocker: true })
          }
        } catch (error) {
          toast.error(`Recipient ${isEditing ? 'update' : 'creation'} failed`, {
            description: getErrorMessage(error),
          })

          return serverFieldErrors(error)
        }
        return undefined
      },
    },
  })

  return (
    <div className="max-w-4xl">
      <form.AppForm>
        <form.Form>
          <div className="grid gap-y-6">
            {recipient && <VerificationSection recipient={recipient} />}
            <Card>
              <CardHeader>
                <CardTitle>
                  <Typography variant="h4">
                    {isEditing ? 'Edit Recipient' : 'Add Recipient'}
                  </Typography>
                </CardTitle>
                <CardDescription>
                  {isEditing
                    ? 'Update this notification recipient'
                    : 'Add a phone number, email address, or fax number to receive notifications'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <form.AppField name="channel">
                      {(field) => (
                        <field.SelectInput
                          id={`${id}-channel`}
                          label="Notification Type"
                          options={channelOptions}
                        />
                      )}
                    </form.AppField>

                    <form.Subscribe selector={(state) => state.values.channel}>
                      {(channel) => {
                        const labelMap = {
                          [NotificationChannelType.Text.Value]: 'Phone Number',
                          [NotificationChannelType.Phone.Value]: 'Phone Number',
                          [NotificationChannelType.Email.Value]: 'Email Address',
                          [NotificationChannelType.Fax.Value]: 'Fax Number',
                        }

                        return (
                          <form.AppField name="value">
                            {(field) =>
                              channel === NotificationChannelType.Email.Value ? (
                                <field.EmailInput label={labelMap[channel] || 'Recipient Value'} />
                              ) : (
                                <field.PhoneInput label={labelMap[channel] || 'Recipient Value'} />
                              )
                            }
                          </form.AppField>
                        )
                      }}
                    </form.Subscribe>
                  </div>

                  <form.AppField name="label">
                    {(field) => (
                      <field.TextInput
                        id={`${id}-label`}
                        label="Label (Optional)"
                        placeholder="e.g., Order Desk, Owner Cell, Office Fax"
                      />
                    )}
                  </form.AppField>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <form.AppField name="enabled">
                  {(field) => (
                    <field.SwitchInput
                      id={`${id}-enabled`}
                      label="Notifications Enabled"
                      description="Enable or disable notifications for this recipient"
                    />
                  )}
                </form.AppField>
              </CardContent>
            </Card>
          </div>

          <Can do={isEditing ? 'edit' : 'create'} on="notification-recipients">
            <SaveBar submitButtonText={isEditing ? 'Update' : 'Save'} />
          </Can>
        </form.Form>
      </form.AppForm>

      {isEditing && (
        <Can do="delete" on="notification-recipients">
          <DeleteButton
            id={recipient?.id}
            model="Notification Recipient"
            mutation={deleteMutation}
            path={path.notifications.index()}
            replace
            onDeleted={deleteMutation.cleanupDeletedNotificationRecipient}
          />
        </Can>
      )}
    </div>
  )
}
